import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { platformAdminApi } from '../api/platformAdminApi';
import { TenantAiKeysCard } from '@/features/chatbot/components/admin/TenantAiKeysCard';
import { TenantEvoHubCredentialsCard } from '@/features/evohub/components/TenantEvoHubCredentialsCard';
import { TenantEmailCard } from '@/features/platform-admin/components/TenantEmailCard';
import {
  sanitizeTenantFeaturesForStorage,
  tenantFeaturesForForm,
} from '@workspace/types';
import {
  DEFAULT_TENANT_CONFIG,
  TENANT_FEATURES,
  TenantFeature,
} from '../types/platformAdmin.types';

type TenantForm = {
  name: string;
  slug: string;
  status: 'ACTIVE' | 'INACTIVE';
  maxUsers: string;
  maxBots: string;
  features: TenantFeature[];
};

function createEmptyForm(): TenantForm {
  return {
    name: '',
    slug: '',
    status: 'ACTIVE',
    maxUsers: String(DEFAULT_TENANT_CONFIG.maxUsers),
    maxBots: String(DEFAULT_TENANT_CONFIG.maxBots),
    features: DEFAULT_TENANT_CONFIG.features,
  };
}

export function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState<TenantForm>(createEmptyForm());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id || isNew) return;
    const loadTenant = async () => {
      try {
        setLoading(true);
        const tenant = await platformAdminApi.getTenant(id);
        setForm({
          name: tenant.name,
          slug: tenant.slug || '',
          status: tenant.status,
          maxUsers: String(tenant.config.maxUsers),
          maxBots: String(tenant.config.maxBots),
          features: tenantFeaturesForForm(tenant.config.features),
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Error al cargar tenant');
      } finally {
        setLoading(false);
      }
    };
    loadTenant();
  }, [id, isNew]);

  const toggleFeature = (feature: TenantFeature, enabled: boolean) => {
    setForm(current => {
      const features = enabled
        ? Array.from(new Set([...current.features, feature]))
        : current.features.filter(item => item !== feature);
      return { ...current, features };
    });
  };

  const handleSave = async () => {
    if (!id || form.features.length === 0) {
      toast.error('Debe haber al menos una feature habilitada');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        status: form.status,
        config: {
          maxUsers: Number(form.maxUsers),
          maxBots: Number(form.maxBots),
          features: sanitizeTenantFeaturesForStorage(form.features),
        },
      };
      if (isNew) {
        await platformAdminApi.createTenant(payload);
        toast.success('Tenant creado');
      } else {
        await platformAdminApi.updateTenant(id, payload);
        toast.success('Tenant actualizado. El correo se configura en la tarjeta Correo de abajo.');
      }
      if (isNew) {
        navigate('/admin/tenants');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al guardar tenant');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className='container mx-auto py-6'>Cargando tenant...</div>;
  }

  return (
    <div className='container mx-auto py-6 max-w-3xl space-y-4'>
      <Button variant='ghost' onClick={() => navigate('/admin/tenants')}>
        <ArrowLeft className='h-4 w-4 mr-2' />
        Volver
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'Nuevo Tenant' : 'Editar Tenant'}</CardTitle>
          <CardDescription>Define límites y features disponibles para el tenant.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={event => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={form.slug}
                onChange={event => setForm({ ...form, slug: event.target.value })}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <select
                value={form.status}
                onChange={event =>
                  setForm({ ...form, status: event.target.value as TenantForm['status'] })
                }
                className='w-full border border-input bg-background rounded-md p-2'
              >
                <option value='ACTIVE'>Activo</option>
                <option value='INACTIVE'>Inactivo</option>
              </select>
            </div>
          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label>Máximo de usuarios activos</Label>
              <Input
                type='number'
                min='0'
                value={form.maxUsers}
                onChange={event => setForm({ ...form, maxUsers: event.target.value })}
              />
            </div>
            <div>
              <Label>Máximo de bots</Label>
              <Input
                type='number'
                min='0'
                value={form.maxBots}
                onChange={event => setForm({ ...form, maxBots: event.target.value })}
              />
            </div>
          </div>

          <div className='space-y-3'>
            <Label>Features</Label>
            <div className='grid gap-3 md:grid-cols-2'>
              {TENANT_FEATURES.map(feature => (
                <div
                  key={feature.value}
                  className='flex items-center justify-between border rounded-md p-3'
                >
                  <span>{feature.label}</span>
                  <Switch
                    checked={form.features.includes(feature.value)}
                    onCheckedChange={enabled => toggleFeature(feature.value, enabled)}
                  />
                </div>
              ))}
            </div>
          </div>

          {!isNew && id && <TenantAiKeysCard tenantId={id} />}
          {!isNew && id && <TenantEvoHubCredentialsCard tenantId={id} />}
          {!isNew && id && <TenantEmailCard tenantId={id} />}

          <div className='flex flex-col items-end gap-2 sm:flex-row sm:justify-end'>
            {!isNew ? (
              <p className='text-sm text-muted-foreground sm:mr-auto sm:max-w-md'>
                Guardar solo aplica nombre, límites y features. Brevo o SendGrid van en la tarjeta Correo.
              </p>
            ) : null}
            <Button onClick={handleSave} disabled={saving}>
              <Save className='h-4 w-4 mr-2' />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
